"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColorToken = void 0;
const lit_1 = require("lit");
const decorators_js_1 = require("lit/decorators.js");
const style_map_js_1 = require("lit/directives/style-map.js");
const surface_styles_1 = require("@/components/common/surface/surface.styles");
const token_values_1 = require("@/components/domains/component/token-values");
const theme_change_controller_1 = require("@/controllers/theme-change-controller");
const shared_styles_1 = require("@/stylesheets/shared.styles");
const color_1 = require("@/utils/color");
require("@/components/common");
// 원시 색 스와치에 태그로 붙이는 역할. 상태 역할끼리는 색이 달라 한 원시 색에 하나만 붙는다.
// 상태를 표현하는 색만 붙이고, 표면·전경·테두리처럼 원시 색을 물려받는 나머지 역할은 각자의 목록에서 소개한다.
const STATUS_ROLES = ['primary', 'accent', 'danger', 'warning'];
/**
 * 색상 토큰 카드.
 * 토큰 이름만 받아 스와치·캡션·태그·명도 대비를 모두 그 이름에서 끌어낸다.
 * 값은 자기 자리에서 계산된 스타일로 읽고, 대비는 화면에 실제로 쓰인 두 색을 재서 붙이므로
 * 색을 바꾸거나 테마를 바꿔도 문서가 따라온다.
 * pairs에 전경색 토큰을 주면 그 표면 위에 유효한 조합을 쌓아 시연한다.
 * 색상 토큰 표를 구성하는 단위로 mm-token-item의 색상 대응물이다.
 */
let ColorToken = class ColorToken extends lit_1.LitElement {
    constructor() {
        super(...arguments);
        this.themeChange = new theme_change_controller_1.ThemeChangeController(this);
        /** 스와치로 보여줄 색상 토큰 이름 (예: gray800, background-subtle-color) */
        this.name = '';
        /** 이 표면 위에 얹는 전경색 토큰 이름, 위에서 아래로 쌓인다. */
        this.pairs = [];
        this.contrasts = [];
    }
    render() {
        return (0, lit_1.html) `
      <figure
        class="swatch"
        style=${(0, style_map_js_1.styleMap)({ '--color-token-background-color': `var(--${this.name})` })}
      >
        ${this.renderRoleTag()} ${this.renderPairs()}
      </figure>
      ${this.renderCaption()}
    `;
    }
    updated() {
        this.measureContrasts();
    }
    renderRoleTag() {
        const role = (0, token_values_1.tokenAliases)(this.name)
            .map(token_values_1.tokenDisplayName)
            .find(alias => STATUS_ROLES.includes(alias));
        if (!role)
            return lit_1.nothing;
        return (0, lit_1.html) `
      <mm-tag>${role}</mm-tag>
    `;
    }
    renderPairs() {
        return this.pairs.map((pair, index) => (0, lit_1.html) `
        <div class="label-row" style=${(0, style_map_js_1.styleMap)({ color: `var(--${pair})` })}>
          <mm-text size="12" weight="bold">${(0, token_values_1.tokenDisplayName)(pair)}</mm-text>
          <span class="contrast">${this.contrasts[index] ?? ''}</span>
        </div>
      `);
    }
    renderCaption() {
        if (!this.name)
            return lit_1.nothing;
        // 시맨틱 토큰은 실제 색이 아니라 물려받는 원시 토큰 이름을 보여준다. 값은 테마마다 바뀌어도
        // 어떤 원시 색을 참조하는지는 구조로 고정되어 있어, 그 구조를 그대로 문서화한다.
        const value = (0, token_values_1.primitiveTokenName)(this.name) ?? (0, token_values_1.computedTokenValue)(this.name, this);
        return (0, lit_1.html) `
      <figcaption>
        <mm-text size="12" weight="bold">${(0, token_values_1.tokenDisplayName)(this.name)}: ${value}</mm-text>
      </figcaption>
    `;
    }
    /** 대비는 브라우저가 실제로 칠한 색끼리 잰다. 토큰이 어떤 단계를 거쳐 왔는지와 무관해진다. */
    measureContrasts() {
        const background = getComputedStyle(this.swatch).backgroundColor;
        const measured = [...this.labelRows].map(row => (0, color_1.contrastRatio)(getComputedStyle(row).color, background));
        if (measured.join() === this.contrasts.join())
            return;
        this.contrasts = measured;
    }
};
ColorToken.styles = [
    shared_styles_1.resetStyles,
    (0, lit_1.css) `
      /* 그리드 한 줄에서 카드 높이가 맞춰지면 스와치가 남는 높이를 가져간다.
         대비쌍이 더 많은 카드 옆에 빈 여백이 남지 않게 한다. */
      :host {
        ${surface_styles_1.surfaceBaseStyles};
        --surface-padding: 0;

        overflow: hidden;
      }

      .swatch {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: flex-end;
        min-height: var(--size-80);
        padding: var(--space-3) var(--space-4);
        background: var(--color-token-background-color);
        box-sizing: border-box;
      }

      /* 이 색을 물려받는 상태 역할 태그: 칩 위쪽에 두고 대비쌍을 아래로 민다. */
      mm-tag {
        margin-bottom: auto;
      }

      .label-row {
        display: flex;
        align-items: baseline;
        gap: var(--space-2);
      }

      /* 명도 대비 값: 라벨 색으로 흐르되 보조 정보라 10px로 낮춘다. */
      .contrast {
        font-size: 10px;
        opacity: 0.7;
      }

      figcaption {
        padding-bottom: var(--space-3);
        margin-left: var(--space-4);
        margin-top: var(--space-2);
        color: var(--foreground-color);
      }
    `,
];
__decorate([
    (0, decorators_js_1.property)({ type: String })
], ColorToken.prototype, "name", void 0);
__decorate([
    (0, decorators_js_1.property)({ attribute: false })
], ColorToken.prototype, "pairs", void 0);
__decorate([
    (0, decorators_js_1.state)()
], ColorToken.prototype, "contrasts", void 0);
__decorate([
    (0, decorators_js_1.query)('.swatch')
], ColorToken.prototype, "swatch", void 0);
__decorate([
    (0, decorators_js_1.queryAll)('.label-row')
], ColorToken.prototype, "labelRows", void 0);
ColorToken = __decorate([
    (0, decorators_js_1.customElement)('mm-color-token')
], ColorToken);
exports.ColorToken = ColorToken;
//# sourceMappingURL=data:application/json;base64,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